import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * 单 tab 编辑锁：同一 projectId 同一时刻仅允许一个 tab 处于"可写"状态，
 * 其他 tab 自动切换为只读，避免陈旧 ReactFlow store 覆盖最新数据。
 *
 * 通道：BroadcastChannel 主，localStorage storage 事件兜底（同源跨窗口）。
 * 语义：后到先得（last claim wins）。新 tab 上线时广播 claim，旧 tab 收到后让位。
 * 用户可点"强制接管"切回可写。
 */

const HEARTBEAT_INTERVAL = 5000;
const HEARTBEAT_TIMEOUT = 15000;

function genTabId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function useEditorTabLock(projectId) {
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [ownerTabId, setOwnerTabId] = useState(null);

  const tabIdRef = useRef(genTabId());
  const bcRef = useRef(null);
  const storageKeyRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const ownerHeartbeatRef = useRef(0);

  const broadcast = useCallback((payload) => {
    const msg = { ...payload, tabId: tabIdRef.current, ts: Date.now() };
    try { bcRef.current?.postMessage(msg); } catch (_) {}
    try {
      if (storageKeyRef.current) {
        localStorage.setItem(storageKeyRef.current, JSON.stringify(msg));
      }
    } catch (_) {}
  }, []);

  const takeOver = useCallback(() => {
    if (!projectId) return;
    setIsReadOnly(false);
    setOwnerTabId(tabIdRef.current);
    ownerHeartbeatRef.current = Date.now();
    broadcast({ type: 'claim', force: true });
  }, [projectId, broadcast]);

  useEffect(() => {
    if (!projectId) return undefined;

    const channelName = `cineart-editor-lock-${projectId}`;
    const storageKey = `cineart-editor-lock:${projectId}`;
    storageKeyRef.current = storageKey;

    const myTabId = tabIdRef.current;
    let bc = null;
    try {
      bc = new BroadcastChannel(channelName);
      bcRef.current = bc;
    } catch (_) {
      bcRef.current = null;
    }

    const handleMessage = (msg) => {
      if (!msg || typeof msg !== 'object') return;
      if (msg.tabId === myTabId) return;

      if (msg.type === 'claim') {
        setIsReadOnly(true);
        setOwnerTabId(msg.tabId);
        ownerHeartbeatRef.current = msg.ts || Date.now();
        try { bc?.postMessage({ type: 'ack', tabId: myTabId, ts: Date.now() }); } catch (_) {}
      } else if (msg.type === 'heartbeat') {
        if (msg.tabId === ownerTabId || (isReadOnly && msg.tabId)) {
          ownerHeartbeatRef.current = msg.ts || Date.now();
        }
      } else if (msg.type === 'release') {
        ownerHeartbeatRef.current = 0;
      }
    };

    const onBcMessage = (e) => handleMessage(e.data);
    const onStorage = (e) => {
      if (e.key !== storageKey || !e.newValue) return;
      try { handleMessage(JSON.parse(e.newValue)); } catch (_) {}
    };

    bc?.addEventListener('message', onBcMessage);
    window.addEventListener('storage', onStorage);

    setIsReadOnly(false);
    setOwnerTabId(myTabId);
    ownerHeartbeatRef.current = Date.now();
    broadcast({ type: 'claim' });

    heartbeatTimerRef.current = setInterval(() => {
      if (!isReadOnly) {
        broadcast({ type: 'heartbeat' });
      } else if (ownerHeartbeatRef.current
        && Date.now() - ownerHeartbeatRef.current > HEARTBEAT_TIMEOUT) {
        setIsReadOnly(false);
        setOwnerTabId(myTabId);
        ownerHeartbeatRef.current = Date.now();
        broadcast({ type: 'claim' });
      }
    }, HEARTBEAT_INTERVAL);

    return () => {
      try {
        if (!isReadOnly) broadcast({ type: 'release' });
      } catch (_) {}
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
      bc?.removeEventListener('message', onBcMessage);
      window.removeEventListener('storage', onStorage);
      try { bc?.close(); } catch (_) {}
      bcRef.current = null;
      storageKeyRef.current = null;
    };
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { isReadOnly, ownerTabId, tabId: tabIdRef.current, takeOver };
}
